// src/pages/Home.js
import React, { useState, useEffect } from 'react';
import '../styles/App.css';
import { sendResendEmail } from '../utils/resend';

// Process stages data
const stages = [
  {
    header: "Ideation.",
    paragraph: "We'll sit down together and discuss what software you want built. Websites, mobile apps, online services; whatever you're thinking we'll draft up a quote for a one time build fee and an ongoing management fee."
  },
  {
    header: "Build & deploy.",
    paragraph: (
      <>
        Under human supervision, our fleet of <strong>coding agents</strong> build your software as you watch in your private Slack or Discord channel. Then, our <strong>deployment agents</strong> review the code and push your software globally for the world to see.
      </>
    )
  },
  {
    header: "Manage & iterate.",
    paragraph: (
      <>
        Our team of <strong>management agents</strong> keep a close eye on your software, implementing patches when they see fit. In your private channel, you can request new features or bug fixes and watch as our agents get to work on them 24/7.
      </>
    )
  }
];

// Project Exploration Visualization Component (Stage 1)
const ProjectExplorationVisualization = () => {
  const [messages, setMessages] = useState([]);
  const [reactions, setReactions] = useState({});

  useEffect(() => {
    // Define conversation flow - only humans chatting, some in threads
    const conversation = [
      { sender: 'human1', delay: 1200 },
      { sender: 'human2', delay: 2400, isThread: true },
      { sender: 'human1', delay: 2800, addReaction: { messageIndex: 0, type: 'heart' } },
      { sender: 'human2', delay: 2100 },
      { sender: 'human1', delay: 2600, isThread: true, addReaction: { messageIndex: 3, type: 'thumbsup' } },
      { sender: 'human2', delay: 2200 },
    ];

    let currentIndex = 0;
    const timeouts = [];

    const addMessage = () => {
      if (currentIndex < conversation.length) {
        const msg = conversation[currentIndex];
        
        // Add the message
        setMessages(prev => [...prev, { 
          id: Date.now() + currentIndex, 
          sender: msg.sender,
          isThread: msg.isThread,
          timestamp: new Date().toLocaleTimeString('en-US', { 
            hour: 'numeric', 
            minute: '2-digit',
            hour12: true 
          })
        }]);

        // Add reaction if specified
        if (msg.addReaction) {
          setTimeout(() => {
            setReactions(prev => ({
              ...prev,
              [msg.addReaction.messageIndex]: msg.addReaction.type
            }));
          }, 800);
        }

        currentIndex++;
        
        if (currentIndex < conversation.length) {
          const timeout = setTimeout(addMessage, conversation[currentIndex].delay);
          timeouts.push(timeout);
        } else {
          // Reset after last message - wait longer to avoid reset before stage rotation
          const resetTimeout = setTimeout(() => {
            setMessages([]);
            setReactions({});
            currentIndex = 0;
            addMessage();
          }, 3500);
          timeouts.push(resetTimeout);
        }
      }
    };

    // Start the conversation
    const initialTimeout = setTimeout(addMessage, 600);
    timeouts.push(initialTimeout);

    return () => {
      timeouts.forEach(timeout => clearTimeout(timeout));
    };
  }, []);

  return (
    <div className="slack-container">
      {/* Slack Sidebar */}
      <div className="slack-sidebar">
        <div className="slack-sidebar-header">
          <div className="slack-workspace-name"></div>
        </div>
        <div className="slack-channels">
          <div className="slack-channels-label">Channels</div>
          <div className="slack-channel-item">
            <span className="slack-hash">#</span>
            <div className="channel-bar"></div>
          </div>
          <div className="slack-channel-item">
            <span className="slack-hash">#</span>
            <div className="channel-bar"></div>
          </div>
          <div className="slack-channel-item active">
            <span className="slack-hash">#</span>
            <span className="channel-name">project-exploration</span>
          </div>
          <div className="slack-channel-item">
            <span className="slack-hash">#</span>
            <div className="channel-bar"></div>
          </div>
          <div className="slack-channel-item">
            <span className="slack-hash">#</span>
            <div className="channel-bar"></div>
          </div>
        </div>
      </div>

      {/* Slack Main Content */}
      <div className="slack-main">
        {/* Slack Channel Header */}
        <div className="slack-header">
          <div className="slack-channel-name">
            <span className="slack-hash">#</span>
            <span className="channel-name">project-exploration</span>
          </div>
        </div>

        {/* Slack Messages */}
        <div className="slack-messages">
          {messages.map((msg, index) => (
            <div key={msg.id} className={`slack-message ${msg.isThread ? 'thread-reply' : ''}`}>
              <div className="slack-avatar human">
                <div className="human-icon"></div>
              </div>
              <div className="slack-message-content">
                <div className="slack-message-header">
                  <div className="slack-username"></div>
                  <div className="slack-timestamp"></div>
                </div>
                <div className="slack-message-text">
                  <div className="message-line" style={{ width: '92%' }}></div>
                  <div className="message-line" style={{ width: '98%' }}></div>
                  <div className="message-line" style={{ width: '85%' }}></div>
                </div>
                {reactions[index] && (
                  <div className="slack-reaction">
                    <span className={`reaction-emoji ${reactions[index]}`}></span>
                    <span className="reaction-count">1</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Slack Visualization Component (Stage 2)
const SlackVisualization = () => {
  const [messages, setMessages] = useState([]);

  useEffect(() => {
    // Agent icons to rotate through
    const agentIcons = [
      '/assets/icons/icon-openai.svg',
      '/assets/icons/icon-cursor.svg',
      '/assets/icons/icon-claude.svg'
    ];

    // Define Slack conversation flow - mostly agents with a couple human messages
    const conversation = [
      { sender: 'agent', agentIcon: agentIcons[0], delay: 800 },
      { sender: 'agent', agentIcon: agentIcons[1], delay: 1900 },
      { sender: 'human', delay: 1200 },
      { sender: 'agent', agentIcon: agentIcons[2], delay: 2400 },
      { sender: 'agent', agentIcon: agentIcons[0], delay: 1600 },
      { sender: 'agent', agentIcon: agentIcons[1], delay: 1800 },
      { sender: 'human', delay: 1500 },
      { sender: 'agent', agentIcon: agentIcons[2], delay: 2200 },
    ];

    let currentIndex = 0;
    const timeouts = [];

    const addMessage = () => {
      if (currentIndex < conversation.length) {
        const msg = conversation[currentIndex];
        setMessages(prev => [...prev, { 
          id: Date.now() + currentIndex, 
          sender: msg.sender,
          agentIcon: msg.agentIcon,
          timestamp: new Date().toLocaleTimeString('en-US', { 
            hour: 'numeric', 
            minute: '2-digit',
            hour12: true 
          })
        }]);
        currentIndex++;
        
        if (currentIndex < conversation.length) {
          const timeout = setTimeout(addMessage, conversation[currentIndex].delay);
          timeouts.push(timeout);
        } else {
          // Reset after last message with longer pause
          const resetTimeout = setTimeout(() => {
            setMessages([]);
            currentIndex = 0;
            addMessage();
          }, 2800);
          timeouts.push(resetTimeout);
        }
      }
    };

    // Start the conversation
    const initialTimeout = setTimeout(addMessage, 600);
    timeouts.push(initialTimeout);

    return () => {
      timeouts.forEach(timeout => clearTimeout(timeout));
    };
  }, []);

  return (
    <div className="slack-container">
      {/* Slack Sidebar */}
      <div className="slack-sidebar">
        <div className="slack-sidebar-header">
          <div className="slack-workspace-name"></div>
        </div>
        <div className="slack-channels">
          <div className="slack-channels-label">Channels</div>
          <div className="slack-channel-item">
            <span className="slack-hash">#</span>
            <div className="channel-bar"></div>
          </div>
          <div className="slack-channel-item">
            <span className="slack-hash">#</span>
            <div className="channel-bar"></div>
          </div>
          <div className="slack-channel-item active">
            <span className="slack-hash">#</span>
            <span className="channel-name">company-agent-workspace</span>
          </div>
          <div className="slack-channel-item">
            <span className="slack-hash">#</span>
            <div className="channel-bar"></div>
          </div>
          <div className="slack-channel-item">
            <span className="slack-hash">#</span>
            <div className="channel-bar"></div>
          </div>
        </div>
      </div>

      {/* Slack Main Content */}
      <div className="slack-main">
        {/* Slack Channel Header */}
        <div className="slack-header">
          <div className="slack-channel-name">
            <span className="slack-hash">#</span>
            <span className="channel-name">company-agent-workspace</span>
          </div>
        </div>

        {/* Slack Messages */}
        <div className="slack-messages">
          {messages.map((msg) => (
            <div key={msg.id} className="slack-message">
              <div className={`slack-avatar ${msg.sender}`}>
                {msg.sender === 'agent' && msg.agentIcon && (
                  <div 
                    className="agent-icon"
                    style={{
                      backgroundColor: '#9CACA6',
                      WebkitMaskImage: `url(${msg.agentIcon})`,
                      WebkitMaskRepeat: 'no-repeat',
                      WebkitMaskPosition: 'center',
                      WebkitMaskSize: 'contain',
                      maskImage: `url(${msg.agentIcon})`,
                      maskRepeat: 'no-repeat',
                      maskPosition: 'center',
                      maskSize: 'contain'
                    }}
                  />
                )}
                {msg.sender === 'human' && (
                  <div className="human-icon"></div>
                )}
              </div>
              <div className="slack-message-content">
                <div className="slack-message-header">
                  <div className="slack-username"></div>
                  {msg.sender === 'agent' && <div className="slack-bot-badge">AGENT</div>}
                  <div className="slack-timestamp"></div>
                </div>
                <div className="slack-message-text">
                  <div className="message-line" style={{ width: '92%' }}></div>
                  <div className="message-line" style={{ width: '98%' }}></div>
                  <div className="message-line" style={{ width: '85%' }}></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Support Visualization Component (Stage 3)
const SupportVisualization = () => {
  const [messages, setMessages] = useState([]);

  useEffect(() => {
    // Agent icons to rotate through
    const agentIcons = [
      '/assets/icons/icon-openai.svg',
      '/assets/icons/icon-cursor.svg',
      '/assets/icons/icon-claude.svg'
    ];

    // Define support conversation flow with threaded messages
    const conversation = [
      { sender: 'human', label: 'BUG REPORT', labelColor: 'red', delay: 1000 },
      { sender: 'agent', agentIcon: agentIcons[0], isThread: true, delay: 2200 },
      { sender: 'agent', agentIcon: agentIcons[1], isThread: true, delay: 2000 },
      { sender: 'human', label: 'FEATURE REQUEST', labelColor: 'blue', delay: 2500 },
      { sender: 'agent', agentIcon: agentIcons[2], isThread: true, delay: 2200 },
      { sender: 'agent', agentIcon: agentIcons[0], isThread: true, delay: 1800 },
      { sender: 'agent', agentIcon: agentIcons[1], isThread: true, delay: 2000 },
    ];

    let currentIndex = 0;
    const timeouts = [];

    const addMessage = () => {
      if (currentIndex < conversation.length) {
        const msg = conversation[currentIndex];
        setMessages(prev => [...prev, { 
          id: Date.now() + currentIndex, 
          sender: msg.sender,
          agentIcon: msg.agentIcon,
          label: msg.label,
          labelColor: msg.labelColor,
          isThread: msg.isThread,
          timestamp: new Date().toLocaleTimeString('en-US', { 
            hour: 'numeric', 
            minute: '2-digit',
            hour12: true 
          })
        }]);
        currentIndex++;
        
        if (currentIndex < conversation.length) {
          const timeout = setTimeout(addMessage, conversation[currentIndex].delay);
          timeouts.push(timeout);
        } else {
          // Reset after last message with longer pause
          const resetTimeout = setTimeout(() => {
            setMessages([]);
            currentIndex = 0;
            addMessage();
          }, 3000);
          timeouts.push(resetTimeout);
        }
      }
    };

    // Start the conversation
    const initialTimeout = setTimeout(addMessage, 800);
    timeouts.push(initialTimeout);

    return () => {
      timeouts.forEach(timeout => clearTimeout(timeout));
    };
  }, []);

  return (
    <div className="slack-container">
      {/* Slack Sidebar */}
      <div className="slack-sidebar">
        <div className="slack-sidebar-header">
          <div className="slack-workspace-name"></div>
        </div>
        <div className="slack-channels">
          <div className="slack-channels-label">Channels</div>
          <div className="slack-channel-item">
            <span className="slack-hash">#</span>
            <div className="channel-bar"></div>
          </div>
          <div className="slack-channel-item">
            <span className="slack-hash">#</span>
            <div className="channel-bar"></div>
          </div>
          <div className="slack-channel-item active">
            <span className="slack-hash">#</span>
            <span className="channel-name">ongoing-support</span>
          </div>
          <div className="slack-channel-item">
            <span className="slack-hash">#</span>
            <div className="channel-bar"></div>
          </div>
          <div className="slack-channel-item">
            <span className="slack-hash">#</span>
            <div className="channel-bar"></div>
          </div>
        </div>
      </div>

      {/* Slack Main Content */}
      <div className="slack-main">
        {/* Slack Channel Header */}
        <div className="slack-header">
          <div className="slack-channel-name">
            <span className="slack-hash">#</span>
            <span className="channel-name">ongoing-support</span>
          </div>
        </div>

        {/* Slack Messages */}
        <div className="slack-messages">
          {messages.map((msg) => (
            <div key={msg.id} className={`slack-message ${msg.isThread ? 'thread-reply' : ''}`}>
              <div className={`slack-avatar ${msg.sender}`}>
                {msg.sender === 'agent' && msg.agentIcon && (
                  <div 
                    className="agent-icon"
                    style={{
                      backgroundColor: '#9CACA6',
                      WebkitMaskImage: `url(${msg.agentIcon})`,
                      WebkitMaskRepeat: 'no-repeat',
                      WebkitMaskPosition: 'center',
                      WebkitMaskSize: 'contain',
                      maskImage: `url(${msg.agentIcon})`,
                      maskRepeat: 'no-repeat',
                      maskPosition: 'center',
                      maskSize: 'contain'
                    }}
                  />
                )}
                {msg.sender === 'human' && (
                  <div className="human-icon"></div>
                )}
              </div>
              <div className="slack-message-content">
                <div className="slack-message-header">
                  <div className="slack-username"></div>
                  {msg.sender === 'agent' && <div className="slack-bot-badge">AGENT</div>}
                  {msg.label && (
                    <div className={`slack-label ${msg.labelColor}`}>
                      {msg.label}
                    </div>
                  )}
                  <div className="slack-timestamp"></div>
                </div>
                <div className="slack-message-text">
                  <div className="message-line" style={{ width: '92%' }}></div>
                  <div className="message-line" style={{ width: '98%' }}></div>
                  <div className="message-line" style={{ width: '85%' }}></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const Home = () => {
  // State for process section
  const [activeStage, setActiveStage] = useState(0);
  
  // State for navigation logo visibility
  const [showNavLogo, setShowNavLogo] = useState(false);

  // State for contact form
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [formStatus, setFormStatus] = useState({
    loading: false,
    success: false,
    error: null
  });

  // Reset to stage 0 when process section comes into view
  useEffect(() => {
    const processSection = document.getElementById('process');
    if (!processSection) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // When the process section comes into view, reset to stage 0
          if (entry.isIntersecting) {
            setActiveStage(0);
          }
        });
      },
      {
        // Trigger when at least 30% of the section is visible
        threshold: 0.3
      }
    );

    observer.observe(processSection);

    return () => {
      observer.disconnect();
    };
  }, []);

  // Auto-rotate stages every 16 seconds (8 seconds on mobile)
  useEffect(() => {
    // Ensure activeStage is valid
    if (activeStage >= stages.length) {
      setActiveStage(0);
    }

    // Use faster rotation on mobile (768px and below)
    const isMobile = window.innerWidth <= 768;
    const rotationInterval = isMobile ? 8000 : 16000;

    const timer = setInterval(() => {
      setActiveStage((prev) => (prev + 1) % stages.length);
    }, rotationInterval);

    return () => clearInterval(timer);
  }, [activeStage]);

  // Handle scroll to show/hide nav logo based on section
  useEffect(() => {
    const heroSection = document.getElementById('hero');
    if (!heroSection) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // Hide logo when hero section is visible, show it when hero is not visible
          setShowNavLogo(!entry.isIntersecting);
        });
      },
      {
        threshold: 0.5 // Trigger when 50% of hero section is visible/hidden
      }
    );

    observer.observe(heroSection);

    return () => {
      observer.disconnect();
    };
  }, []);

  // Smooth scroll to section
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Handle stage click
  const handleStageClick = (stageIndex) => {
    setActiveStage(stageIndex);
  };

  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (formStatus.error) {
      setFormStatus(prev => ({ ...prev, error: null }));
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Reset form status
    setFormStatus({
      loading: true,
      success: false,
      error: null
    });

    try {
      await sendResendEmail(formData);
      
      // Success - reset form and show success message
      setFormData({
        name: '',
        email: '',
        message: ''
      });
      setFormStatus({
        loading: false,
        success: true,
        error: null
      });

      // Clear success message after 5 seconds
      setTimeout(() => {
        setFormStatus(prev => ({ ...prev, success: false }));
      }, 5000);
    } catch (error) {
      setFormStatus({
        loading: false,
        success: false,
        error: error.message || 'Failed to send message. Please try again.'
      });
    }
  };

  // HandyCo logo component
  const HandIcon = () => (
    <div
      role="img"
      aria-label="HandyCo Logo"
      style={{
        width: '100%',
        height: '100%',
        backgroundColor: '#9CACA6',
        WebkitMaskImage: 'url(/assets/icons/icon_handyco.svg)',
        WebkitMaskRepeat: 'no-repeat',
        WebkitMaskPosition: 'center',
        WebkitMaskSize: 'contain',
        maskImage: 'url(/assets/icons/icon_handyco.svg)',
        maskRepeat: 'no-repeat',
        maskPosition: 'center',
        maskSize: 'contain'
      }}
    />
  );

  // Agent logo component with tooltip
  const AgentLogo = ({ name, iconPath, tooltip, tooltipLines }) => {
    // Adjust mask size for Linear icon to compensate for SVG spacing
    const maskSize = name === 'Linear' ? '120%' : 'contain';
    
    return (
      <div 
        className="agent-logo-container"
        style={{
          width: '100px',
          height: '100px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'transform 0.3s ease, opacity 0.3s ease',
          padding: '12px',
          position: 'relative'
        }}
      >
        <div
          role="img"
          aria-label={`${name} logo`}
          style={{
            width: '100%',
            height: '100%',
            backgroundColor: '#9CACA6',
            WebkitMaskImage: `url(${iconPath})`,
            WebkitMaskRepeat: 'no-repeat',
            WebkitMaskPosition: 'center',
            WebkitMaskSize: maskSize,
            maskImage: `url(${iconPath})`,
            maskRepeat: 'no-repeat',
            maskPosition: 'center',
            maskSize: maskSize,
            // Ensure consistent sizing for all icons
            minHeight: '56px',
            minWidth: '56px'
          }}
        />
        <div className="agent-tooltip">
          {tooltipLines ? (
            tooltipLines.map((line, index) => (
              <div key={index} className="tooltip-line">
                <span className="tooltip-bullet">?</span>
                <span>{line.text}</span>
              </div>
            ))
          ) : (
            <div className="tooltip-line">
              <span className="tooltip-bullet">?</span>
              <span>{tooltip.text}</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="home-container">
      {/* Navigation */}
      <nav className="navigation">
        <div 
          className={`nav-logo ${showNavLogo ? 'visible' : ''}`}
          onClick={() => scrollToSection('hero')}
        >
          <div style={{ width: '80px', height: '80px' }}>
            <HandIcon />
          </div>
        </div>
        <div className="nav-links">
          <button onClick={() => scrollToSection('about')} className="nav-link">About</button>
          <button onClick={() => scrollToSection('contact')} className="nav-link">Contact</button>
        </div>
      </nav>

      {/* First Screen - Hero */}
      <section id="hero" className="hero-section">
        <div className="hero-content">
          <div style={{ width: '250px', height: '250px', fontSize: '250px' }}>
            <HandIcon />
          </div>
          <h1 className="hero-title">HANDY CO</h1>
          <p className="hero-subtitle">A modern development house.</p>
          <button 
            className="scroll-arrow" 
            onClick={() => scrollToSection('about')}
            aria-label="Scroll to about section"
          >
            Tell me more
          </button>
        </div>
      </section>

      {/* Second Screen - About */}
      <section id="about" className="about-section">
        <div className="about-content">
          <h2 className="about-title">We use agents to accelerate software development.</h2>
          <p className="about-description">
            Our agents restlessly build, deploy, debug, and manage your site or app. Always on, always available, always working.
          </p>
          <div className="agent-logos">
            <AgentLogo 
              name="OpenAI" 
              iconPath="/assets/icons/icon-openai.svg" 
              tooltipLines={[
                { text: "gpt-5-codex-high" },
                { text: "gpt-5-high" }
              ]} 
            />
            <AgentLogo 
              name="Cursor" 
              iconPath="/assets/icons/icon-cursor.svg" 
              tooltipLines={[
                { text: "cloud agent" },
                { text: "bug bot" }
              ]} 
            />
            <AgentLogo 
              name="Claude" 
              iconPath="/assets/icons/icon-claude.svg" 
              tooltip={{ text: "claude-4.5-sonnett" }} 
            />
            <AgentLogo 
              name="Linear" 
              iconPath="/assets/icons/icon-linear.svg" 
              tooltipLines={[
                { text: "Linear agent for Slack" },
                { text: "Linear agent for Discord" }
              ]} 
            />
          </div>
          <button 
            className="scroll-arrow" 
            onClick={() => scrollToSection('process')}
            aria-label="Scroll to process section"
          >
            How we work
          </button>
        </div>
      </section>

      {/* Third Screen - Process */}
      <section id="process" className="process-section">
        <div className="process-content">
          {/* Left Column - Stages */}
          <div className="process-stages">
            {stages.map((stage, index) => (
              <div
                key={index}
                className={`process-stage ${activeStage === index ? 'active' : ''}`}
                onClick={() => handleStageClick(index)}
              >
                <h3 className="stage-header">{stage.header}</h3>
                <p className="stage-paragraph">{stage.paragraph}</p>
              </div>
            ))}
          </div>

          {/* Right Column - Browser Mockup */}
          <div className="process-browser">
            <div className="browser-mockup">
              <div className="browser-header">
                <div className="browser-dots">
                  <span className="dot"></span>
                  <span className="dot"></span>
                  <span className="dot"></span>
                </div>
              </div>
              <div className="browser-content">
                {activeStage === 0 && <ProjectExplorationVisualization />}
                {activeStage === 1 && <SlackVisualization />}
                {activeStage === 2 && <SupportVisualization />}
              </div>
            </div>
          </div>
        </div>
        <button 
          className="scroll-arrow" 
          onClick={() => scrollToSection('contact')}
          aria-label="Scroll to contact section"
        >
          Contact us
        </button>
      </section>

      {/* Fourth Screen - Contact */}
      <section id="contact" className="contact-section">
        <div className="contact-content">
          <h2 className="contact-title">Contact us.</h2>
          <p className="contact-description">
            Reach out and tell us what our agents can build for you.
          </p>
          <form className="contact-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <input 
                type="text" 
                name="name"
                className="form-input" 
                placeholder="Name"
                value={formData.name}
                onChange={handleInputChange}
                required 
                disabled={formStatus.loading}
              />
            </div>
            <div className="form-group">
              <input 
                type="email" 
                name="email"
                className="form-input" 
                placeholder="Email"
                value={formData.email}
                onChange={handleInputChange}
                required 
                disabled={formStatus.loading}
              />
            </div>
            <div className="form-group">
              <textarea 
                name="message"
                className="form-input form-textarea" 
                placeholder="Tell us about your project"
                rows="5"
                value={formData.message}
                onChange={handleInputChange}
                required
                disabled={formStatus.loading}
              />
            </div>
            {formStatus.error && (
              <div className="form-error" style={{
                color: '#E88888',
                fontSize: '14px',
                marginBottom: '10px',
                fontFamily: 'Lato, sans-serif'
              }}>
                {formStatus.error}
              </div>
            )}
            {formStatus.success && (
              <div className="form-success" style={{
                color: '#9CACA6',
                fontSize: '14px',
                marginBottom: '10px',
                fontFamily: 'Lato, sans-serif'
              }}>
                Message sent successfully! We'll get back to you soon.
              </div>
            )}
            <button 
              type="submit" 
              className="form-submit"
              disabled={formStatus.loading}
            >
              {formStatus.loading ? 'Sending...' : 'Send Message'}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default Home;